import { throttle } from 'lodash-es';
import { useRef, useEffect, useState } from 'react';

import { sanitizeUrl } from '@xing-com/crate-people-directory-utils';
import { IconArrowRight, IconArrowLeft } from '@xing-com/icons';

import {
  SPECIAL_CHARACTER_ID,
  SPECIAL_CHARACTER_LABEL,
  NAVIGATION_ITEMS_ARRAY,
  DATA_TEST_IDS,
} from './constants';
import { isBrowser } from './helper';
import {
  Container,
  LetterContainer,
  LetterText,
  LettersListContainer,
  ArrowContainerLeft,
  ArrowContainerRight,
} from './letter-navigation.styles';
import { PEOPLE_DIRECTORY_ROUTE } from './routes';

type LetterNavigationProps = {
  activeLetter?: string;
};

export const LetterNavigation: React.FC<LetterNavigationProps> = ({
  activeLetter,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [leftArrowDisabled, setLeftArrowDisabled] = useState(false);
  const [rightArrowDisabled, setRightArrowDisabled] = useState(false);

  const updateArrowsStatus = (element: HTMLDivElement): void => {
    setLeftArrowDisabled(element.scrollLeft === 0);
    setRightArrowDisabled(
      element.scrollWidth - element.scrollLeft - element.clientWidth < 1
    );
  };

  useEffect(() => {
    if (!containerRef || !containerRef.current) return;

    const containerElement = containerRef.current;

    updateArrowsStatus(containerElement);

    const eventHandler = throttle(
      () => updateArrowsStatus(containerElement),
      100
    );

    if (isBrowser()) {
      window.addEventListener('resize', eventHandler);
    }
    containerElement.addEventListener('scroll', eventHandler);

    return () => {
      window?.removeEventListener('resize', eventHandler);
      containerElement.removeEventListener('scroll', eventHandler);
    };
  }, [containerRef]);

  const onLeftArrowClick = (): void => {
    if (containerRef && containerRef.current) {
      containerRef.current.scrollLeft -= containerRef.current.clientWidth;
    }
  };
  const onRightArrowClick = (): void => {
    if (containerRef && containerRef.current) {
      containerRef.current.scrollLeft += containerRef.current.clientWidth;
    }
  };

  const getLettersList = (): React.ReactNode =>
    NAVIGATION_ITEMS_ARRAY.map((letter: string) => {
      const isActive = letter === activeLetter;

      const label =
        letter === SPECIAL_CHARACTER_ID
          ? SPECIAL_CHARACTER_LABEL
          : letter.toUpperCase();

      return (
        <LetterContainer
          key={letter}
          $isActive={isActive}
          to={sanitizeUrl(`${PEOPLE_DIRECTORY_ROUTE}${letter}`)}
          data-test-id={`${DATA_TEST_IDS.letterNavigationButton}_${letter}`}
        >
          <LetterText
            noMargin
            size="large"
            fontWeight="bold"
            $isActive={isActive}
          >
            {label}
          </LetterText>
        </LetterContainer>
      );
    });

  return (
    <Container>
      <ArrowContainerLeft
        $isDisabled={leftArrowDisabled}
        onClick={onLeftArrowClick}
      >
        <IconArrowLeft />
      </ArrowContainerLeft>
      <LettersListContainer ref={containerRef}>
        {getLettersList()}
      </LettersListContainer>
      <ArrowContainerRight
        $isDisabled={rightArrowDisabled}
        onClick={onRightArrowClick}
      >
        <IconArrowRight />
      </ArrowContainerRight>
    </Container>
  );
};
